import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Chip,
  Fade,
  Zoom,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  IconButton,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { io } from 'socket.io-client';
import { DOMAIN, REST_API, REST_API_IO } from '../constants/Defaultvalues';
import { CheckCircle, Error, PlayCircleOutline, RadioButtonUnchecked, Close } from '@material-ui/icons';

const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: '0 auto',
  minWidth: 500,
  maxWidth: 800,
}));

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  '&.MuiStepConnector-root': {
    top: 20,
    left: 'calc(-50% + 20px)',
    right: 'calc(50% + 20px)',
  },
  '&.MuiStepConnector-line': {
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const StepIcon = styled(Box)(({ theme, status }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 
    status === 'completed' ? theme.palette.success.main :
    status === 'error' ? theme.palette.error.main :
    status === 'active' ? theme.palette.primary.main :
    theme.palette.grey[300],
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const StepsContainer = styled(Box)(({ theme }) => ({
  maxHeight: 200,
  overflowY: 'auto',
  padding: theme.spacing(2),
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
}));

const RequestProgressModal = ({ open, onClose, requestData, onSocketReady, heading }) => {
  // Manual cleanup function
  const cleanupSocket = () => {
    console.log('[PROGRESS-MODAL] 🧹 cleanupSocket called, socketRef.current:', socketRef.current);
    if (socketRef.current) {
      console.log('[PROGRESS-MODAL] 🧹 Manual cleanup of socket connection...');
      socketRef.current.disconnect();
      socketRef.current = null;
      shouldStayConnected.current = false;
      setSocket(null);
      console.log('[PROGRESS-MODAL] 🧹 Socket cleanup completed');
    } else {
      console.log('[PROGRESS-MODAL] 🧹 No socket to cleanup');
    }
  };
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [steps, setSteps] = useState([]);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [errorSteps, setErrorSteps] = useState(new Set());
  const [socket, setSocket] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [socketId, setSocketId] = useState(null);
  const socketRef = useRef(null);
  const stepsContainerRef = useRef(null);
  const shouldStayConnected = useRef(false);
  const timeoutRefs = useRef([]);

  // Reset progress when new requestData is received (for continuous operations)
  useEffect(() => {
    if (requestData && open) {
      console.log('[PROGRESS-MODAL] 🔄 New request data received, resetting progress...');
      setSteps([]);
      setCompletedSteps(new Set()); // Keep as Set, not number
      setErrorSteps(new Set());
      setIsComplete(false);
      setProgress(0);
    }
  }, [requestData, open]);

  useEffect(() => {
    if (open && !socketRef.current && shouldStayConnected.current === false) {
      shouldStayConnected.current = true;
      console.log('[PROGRESS-MODAL] 🚀 Initializing socket connection...');
      
      // Connect directly to the working socket server (same as chat)
      console.log('[PROGRESS-MODAL] 🚀 Connecting to working socket server...');
      initializeSocketConnection();

       function initializeSocketConnection() {
         // Initialize Socket.IO connection to working socket server (same as chat)
         socketRef.current = io(REST_API_IO, {
           path: "/socket.io",
           transports: ["websocket", "polling"],
           secure: true
         });
        setupSocketListeners();
      }

       function initializeFallbackConnection() {
         // Initialize Socket.IO connection to original socket server
         console.log('[PROGRESS-MODAL] 🔄 Using fallback to original socket server...');
         socketRef.current = io(REST_API_IO, {
          path: "/socket.io",
          transports: ["websocket"],
          secure: true,
          forceNew: true,
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          withCredentials: false,
          autoConnect: true,
          upgrade: true,
          rememberUpgrade: true
        });
        setupSocketListeners();
      }

      function setupSocketListeners() {
        socketRef.current.on('connect', () => {
        console.log('[PROGRESS-MODAL] 🔌 Connected to progress tracking socket:', socketRef.current.id);
        setSocket(socketRef.current);
        setSocketId(socketRef.current.id);
        
        // Emit the socket ID to parent component
        if (requestData) {
          requestData.socketId = socketRef.current.id;
        }

        console.log('[PROGRESS-MODAL] 🔌 Socket connection established successfully');
        
        // Test if we can emit and receive messages
        console.log('[PROGRESS-MODAL] 🧪 Testing socket communication...');
        socketRef.current.emit('test_progress', { 
          message: 'Frontend test message', 
          userId: JSON.parse(localStorage.getItem('user'))?.user_id 
        });
        
        // Test if we can receive any events at all
        console.log('[PROGRESS-MODAL] 🧪 Testing if we can receive ANY events...');
        setTimeout(() => {
          console.log('[PROGRESS-MODAL] 🧪 No events received in 5 seconds - connection might be one-way');
        }, 5000);
        
        // Notify parent that socket is ready with socket ID
        if (onSocketReady) {
          setTimeout(() => {
            onSocketReady(true);
          }, 500); // Small delay to ensure connection is stable
        }
      });

      // Add debugging for all socket events
      socketRef.current.onAny((eventName, ...args) => {
        console.log('[PROGRESS-MODAL] 🔌 Socket event received:', eventName, args);
        if (eventName === 'request_progress') {
          console.log('[PROGRESS-MODAL] 🎉 FOUND request_progress event!', args);
        }
      });

        // Test if we can emit to the socket
        console.log('[PROGRESS-MODAL] 🧪 Testing frontend socket emission...');
        socketRef.current.emit('test_progress', { 
          message: 'Frontend test message', 
          userId: JSON.parse(localStorage.getItem('user'))?.user_id,
          domain: DOMAIN
        });

        // Test if we can receive any events at all
        console.log('[PROGRESS-MODAL] 🧪 Testing event reception...');
        setTimeout(() => {
          console.log('[PROGRESS-MODAL] 🧪 5 seconds passed - checking if any events were received');
        }, 5000);

      // Listen for test events
      socketRef.current.on('test_progress', (data) => {
        console.log('[PROGRESS-MODAL] 🧪 Test progress event received:', data);
      });

      // Listen for message events (same as chat) to catch progress messages
      socketRef.current.on('message', (data) => {
        console.log('[PROGRESS-MODAL] 📨 Message event received:', data);
        if (data.type === 'progress') {
          console.log('[PROGRESS-MODAL] 🎉 Found progress message in message event!', data);
          // Process as progress message
          handleProgressMessage(data);
        }
      });

      // Function to handle progress messages
      const handleProgressMessage = (data) => {
        console.log('[PROGRESS-MODAL] 📊 Processing progress message:', data);
        console.log('[PROGRESS-MODAL] 📊 Data userId:', data.userId);
        console.log('[PROGRESS-MODAL] 📊 Data domain:', data.domain);
        
        // Filter by domain (same as chat)
        if (data.domain !== DOMAIN) {
          console.log('[PROGRESS-MODAL] 📊 Ignoring progress update for different domain:', data.domain);
          return;
        }
        
        // Filter by current user ID
        const currentUserId = JSON.parse(localStorage.getItem('user'))?.user_id;
        console.log('[PROGRESS-MODAL] 📊 User ID filtering - Data userId:', data.userId, 'Current userId:', currentUserId);
        
        if (data.userId && data.userId !== currentUserId) {
          console.log('[PROGRESS-MODAL] 📊 Ignoring progress update for different user:', data.userId, 'vs current:', currentUserId);
          return;
        }
        
        console.log('[PROGRESS-MODAL] 📊 Processing progress update for current user');
        setProgress(data.progress);
        setCurrentStep(data.message);
        
        // If estimate is complete, mark all steps as completed
        if (data.step === 'complete' && data.progress === 100) {
          console.log('[PROGRESS-MODAL] 🎉 Estimate complete! Marking all steps as completed');
          setSteps(prevSteps => {
            const updatedSteps = prevSteps.map(step => ({
              ...step,
              status: 'completed'
            }));
            // Mark all steps as completed
            setCompletedSteps(prev => new Set([...prev, ...updatedSteps.map(step => step.id)]));
            return updatedSteps;
          });
        }
        
        // Add new step if it doesn't exist
        if (data.step && !steps.find(step => step.id === data.step)) {
          const newStep = {
            id: data.step,
            label: data.message || 'Processing...',
            status: 'active'
          };
          
          setSteps(prevSteps => {
            const exists = prevSteps.find(step => step.id === data.step);
            if (!exists) {
              console.log('[PROGRESS-MODAL] 📊 Adding new step:', newStep);
              return [...prevSteps, newStep];
            }
            return prevSteps;
          });
        }
        
        // Update existing step
        if (data.step) {
          setSteps(prevSteps => 
            prevSteps.map(step => {
              if (step.id === data.step) {
                const updatedStep = {
                  ...step,
                  label: data.message || step.label,
                  status: data.step.endsWith('_error') ? 'error' : 
                         data.step.endsWith('_success') ? 'completed' : 
                         data.step === 'complete' ? 'completed' : 'active'
                };
                console.log('[PROGRESS-MODAL] 📊 Updating step:', updatedStep);
                return updatedStep;
              }
              return step;
            })
          );
          
          // Mark previous steps as completed
          setSteps(prevSteps => {
            const currentStepIndex = prevSteps.findIndex(step => step.id === data.step);
            if (currentStepIndex > 0) {
              return prevSteps.map((step, index) => {
                if (index < currentStepIndex && step.status === 'active') {
                  console.log('[PROGRESS-MODAL] 📊 Marking previous step as completed:', step.id);
                  return { ...step, status: 'completed' };
                }
                return step;
              });
            }
            return prevSteps;
          });
          
          // Update completed and error steps
          if (data.step.endsWith('_error')) {
            setErrorSteps(prev => new Set([...prev, data.step]));
          } else if (data.step.endsWith('_success') || data.step === 'complete') {
            setCompletedSteps(prev => new Set([...prev, data.step]));
          }
        }
        
        // Auto-scroll to bottom to show latest messages
        setTimeout(() => {
          if (stepsContainerRef.current) {
            stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
          }
        }, 100);
      };

      socketRef.current.on('request_progress', (data) => {
        console.log('[PROGRESS-MODAL] 📊 Progress update received:', data);
        console.log('[PROGRESS-MODAL] 📊 Current user ID from localStorage:', JSON.parse(localStorage.getItem('user'))?.user_id);
        console.log('[PROGRESS-MODAL] 📊 Data userId:', data.userId);
        console.log('[PROGRESS-MODAL] 📊 Data domain:', data.domain);
        
        // Filter by domain (same as chat)
        if (data.domain !== DOMAIN) {
          console.log('[PROGRESS-MODAL] 📊 Ignoring progress update for different domain:', data.domain);
          return;
        }
        
        // Filter by current user ID - TEMPORARILY DISABLED FOR DEBUGGING
        const currentUserId = JSON.parse(localStorage.getItem('user'))?.user_id;
        console.log('[PROGRESS-MODAL] 📊 User ID filtering - Data userId:', data.userId, 'Current userId:', currentUserId);
        
        // Enable filtering to test
        if (data.userId && data.userId !== currentUserId) {
          console.log('[PROGRESS-MODAL] 📊 Ignoring progress update for different user:', data.userId, 'vs current:', currentUserId);
          return;
        }
        
        console.log('[PROGRESS-MODAL] 📊 Processing progress update for current user');
        setProgress(data.progress);
        setCurrentStep(data.message);
        
        // Add new step if it doesn't exist
        setSteps(prevSteps => {
          const existingStep = prevSteps.find(step => step.id === data.step);
          if (!existingStep) {
            const newSteps = [...prevSteps, { 
              id: data.step, 
              label: data.message, 
              status: 'active' 
            }];
            
            // Auto-scroll to the new step
            setTimeout(() => {
              if (stepsContainerRef.current) {
                stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
              }
            }, 100);
            
            return newSteps;
          }
          return prevSteps;
        });

        // Handle error states
        if (data.step.includes('_error')) {
          setErrorSteps(prevErrors => new Set([...prevErrors, data.step]));
          // Auto-scroll to error step
          setTimeout(() => {
            if (stepsContainerRef.current) {
              stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
            }
          }, 100);
        } else if (data.step.includes('_success')) {
          setCompletedSteps(prevCompleted => new Set([...prevCompleted, data.step]));
          // Auto-scroll to completed step
          setTimeout(() => {
            if (stepsContainerRef.current) {
              stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
            }
          }, 100);
        } else {
          // Mark previous steps as completed (only if not error/success)
          setCompletedSteps(prevCompleted => {
            const newCompleted = new Set(prevCompleted);
            setSteps(currentSteps => {
              const currentStepIndex = currentSteps.findIndex(step => step.id === data.step);
              currentSteps.forEach((step, index) => {
                if (index < currentStepIndex && !step.id.includes('_error') && !step.id.includes('_success')) {
                  newCompleted.add(step.id);
                }
              });
              return currentSteps;
            });
            return newCompleted;
          });
        }

        if (data.step === 'complete') {
          console.log('[PROGRESS-MODAL] 🎉 Complete step received in request_progress handler');
          setIsComplete(true);
          setTimeout(() => {
            console.log('[PROGRESS-MODAL] 🧹 Auto-closing modal after 2 seconds...');
            cleanupSocket();
            console.log('[PROGRESS-MODAL] 🧹 Calling onClose...');
            onClose();
            console.log('[PROGRESS-MODAL] 🧹 onClose called');
          }, 2000);
        }
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('[PROGRESS-MODAL] ❌ Disconnected from progress tracking socket. Reason:', reason);
        setSocket(null);
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('[PROGRESS-MODAL] ❌ Socket connection error:', error);
        console.error('[PROGRESS-MODAL] ❌ Error details:', {
          message: error.message,
          description: error.description,
          context: error.context,
          type: error.type
        });
        
        // Try fallback to original socket server
        console.log('[PROGRESS-MODAL] 🔄 Trying fallback to original socket server...');
        if (socketRef.current) {
          socketRef.current.disconnect();
        }
        
        // Try connecting to the original socket server
        socketRef.current = io(REST_API_IO, {
          path: "/socket.io",
          transports: ["websocket"],
          secure: true,
          forceNew: true,
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          withCredentials: false,
          autoConnect: true,
          upgrade: true,
          rememberUpgrade: true
        });
        setupSocketListeners();
        
        setSocket(null);
      });

      socketRef.current.on('reconnect', (attemptNumber) => {
        console.log('[PROGRESS-MODAL] 🔄 Reconnected to socket after', attemptNumber, 'attempts');
        setSocket(socketRef.current);
      });

      // Add connection timeout handler
      setTimeout(() => {
        if (!socketRef.current || !socketRef.current.connected) {
          console.log('[PROGRESS-MODAL] ⏰ Connection timeout - socket not connected after 10 seconds');
          console.log('[PROGRESS-MODAL] ⏰ Socket state:', socketRef.current ? socketRef.current.connected : 'null');
        }
      }, 10000);

      socketRef.current.on('reconnect_error', (error) => {
        console.error('[PROGRESS-MODAL] ❌ Reconnection error:', error);
      });

      socketRef.current.on('reconnect_failed', () => {
        console.error('[PROGRESS-MODAL] ❌ Failed to reconnect to socket');
        setSocket(null);
        });
      }
    }

    return () => {
      // Only cleanup if modal is being closed
      if (!open && socketRef.current) {
        console.log('[PROGRESS-MODAL] 🧹 Cleaning up socket connection...');
        socketRef.current.disconnect();
        socketRef.current = null;
        shouldStayConnected.current = false;
      }
    };
  }, [open]); // Removed onClose from dependencies

  // Auto-scroll to bottom when new steps are added
  useEffect(() => {
    if (steps.length > 0 && stepsContainerRef.current) {
      const timeoutId = setTimeout(() => {
        if (stepsContainerRef.current) {
          stepsContainerRef.current.scrollTop = stepsContainerRef.current.scrollHeight;
        }
      }, 100);
      
      // Store timeout ID for cleanup
      timeoutRefs.current.push(timeoutId);
      
      // Cleanup function
      return () => {
        clearTimeout(timeoutId);
        timeoutRefs.current = timeoutRefs.current.filter(id => id !== timeoutId);
      };
    }
  }, [steps.length]);

  // Cleanup all timeouts when component unmounts
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutRefs.current = [];
    };
  }, []);

  // Ensure body scroll is restored when modal closes
  useEffect(() => {
    if (!open) {
      // Force restore body scroll when modal closes
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [open]);

  const getStepStatus = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    if (!step) return 'pending';
    
    // Check if step has error status
    if (step.status === 'error' || errorSteps.has(stepId)) return 'error';
    
    // Check if step is completed
    if (step.status === 'completed' || completedSteps.has(stepId)) return 'completed';
    
    // Check if step is active
    if (step.status === 'active') return 'active';
    
    return 'pending';
  };

  const getStepIcon = (stepId) => {
    const status = getStepStatus(stepId);
    return (
      <StepIcon status={status}>
        {status === 'completed' && <CheckCircle />}
        {status === 'error' && <Error />}
        {status === 'active' && <PlayCircleOutline />}
        {status === 'pending' && <RadioButtonUnchecked />}
      </StepIcon>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {}} // Empty function - no closing allowed
      // onClose={(event, reason) => {
      //   // Only allow closing via the close button, not by clicking outside
      //   if (reason === 'backdropClick') {
      //     return; // Do nothing - prevent closing
      //   }
      //   onClose(); // Allow closing via escape key or close button
      // }}
      maxWidth="md"
      fullWidth
      disableScrollLock={true}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1, position: 'relative' }}>
        {/* Close button - only show when socket is not connected */}
        {!socket && (
          <IconButton
            aria-label="close"
            onClick={() => {
              cleanupSocket();
              onClose();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              '&:hover': {
                color: (theme) => theme.palette.grey[700],
              },
            }}
          >
            <Close />
          </IconButton>
        )}
        <Typography variant="h6" component="div">
          {/* Creating {requestData?.quotation === 1 ? 'Estimate' : 'Sales Order'} */}
          {heading}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we process your request...
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <ProgressContainer>
          {/* Overall Progress */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Overall Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)'
                }
              }} 
            />
          </Box>

          {/* Current Step */}
          {currentStep && (
            <Fade in={true}>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="primary" fontWeight="medium">
                  {currentStep}
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Step List */}
          {steps.length > 0 && (
            <StepsContainer ref={stepsContainerRef}>
              <Stepper 
                orientation="vertical" 
                connector={<CustomStepConnector />}
                sx={{ 
                  '& .MuiStepLabel-root': {
                    padding: 0,
                  },
                  '& .MuiStepLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                  '& .MuiStepLabel-label.Mui-active': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                  '& .MuiStepLabel-label.Mui-completed': {
                    color: 'success.main',
                    fontWeight: 600,
                  },
                  '& .MuiStepLabel-label.Mui-error': {
                    color: 'error.main',
                    fontWeight: 600,
                  },
                }}
              >
                {steps.map((step, index) => (
                  <Step 
                    key={step.id} 
                    active={getStepStatus(step.id) === 'active'}
                    completed={getStepStatus(step.id) === 'completed'}
                    sx={{
                      '& .MuiStepLabel-iconContainer': {
                        paddingRight: 2,
                      },
                    }}
                  >
                    <StepLabel
                      StepIconComponent={() => getStepIcon(step.id)}
                      error={getStepStatus(step.id) === 'error'}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getStepStatus(step.id) === 'error' ? 'error.main' :
                                 getStepStatus(step.id) === 'completed' ? 'success.main' : 
                                 getStepStatus(step.id) === 'active' ? 'primary.main' : 'text.secondary',
                          fontWeight: getStepStatus(step.id) === 'active' ? 600 : 400,
                        }}
                      >
                        {step.label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </StepsContainer>
          )}

          {/* Connection Status */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {socket ? '🟢' : '🔴'}
              {/* Socket Status: {socket ? '🟢 Connected' : '🔴 Disconnected'} */}
            </Typography>
            {/* {socketId && (
              <Typography variant="caption" color="text.secondary">
                ID: {socketId}
              </Typography>
            )} */}
          
          </Box>

          {/* Completion Message */}
          {isComplete && (
            <Fade in={true}>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  ✅ Successfully Completed!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Redirecting you back...
                </Typography>
              </Box>
            </Fade>
          )}
        </ProgressContainer>
      </DialogContent>
    </Dialog>
  );
};

export default RequestProgressModal;
